'use client';

import { useState, useMemo } from 'react';
import Department from './_Department';
import FilterDropdown from '@components/FilterDropdown';

export interface JobProps {
  absolute_url: string;
  data_compliance: [
    {
      type: string;
      requires_consent: boolean;
      requires_processing_consent: boolean;
      requires_retention_consent: boolean;
      retention_period?: string;
      demographic_data_consent_applies: boolean;
    },
  ];
  internal_job_id: number;
  location: string;
  metadata: string;
  id: number;
  updated_at: string;
  requisition_id: string;
  title: string;
  company_name: string;
  first_published: string;
  department: string;
}

interface DepartmentJobs {
  name: string;
  jobs: JobProps[];
}

interface CareerJobPostingProps {
  initialJobs: Array<JobProps>;
  initialDepartments: Array<string>;
  initialOffices: Array<string>;
  jobPostings?: string;
}

export function CareerPosting({
  initialJobs,
  initialDepartments,
  initialOffices,
  jobPostings,
}: CareerJobPostingProps) {
  const [deptFilter, setDeptFilter] = useState<string>('');
  const [officeFilter, setOfficeFilter] = useState<string>('');

  const departmentJobs = useMemo<Array<DepartmentJobs>>(
    () =>
      initialDepartments.map((department) => ({
        name: department,
        jobs: initialJobs.filter((j) => j.department === department),
      })),
    [initialDepartments, initialJobs],
  );

  const displayJobs = useMemo(() => {
    let filteredJobs = [...departmentJobs];

    if (deptFilter !== '') {
      filteredJobs = filteredJobs.filter((value) => value.name === deptFilter);
    }

    if (officeFilter !== '') {
      filteredJobs = filteredJobs
        .map((department) => ({
          ...department,
          jobs: department.jobs.filter((job) => job.location === officeFilter),
        }))
        .filter((dept) => dept.jobs.length > 0);
    }

    return filteredJobs;
  }, [deptFilter, officeFilter, departmentJobs]);

  return (
    <section>
      <div>
        <h1 className="tra-text-web-heading-2 py-10 mb-10 text-center bg-warm-200">
          {jobPostings}
        </h1>
        <h2 className="tra-text-web-heading-3 mb-6 text-center">Traeger Wood Pellet Grills</h2>
        <h3 className="tra-text-web-heading-h4 mb-10 text-center">Current Job Openings</h3>
        <div className="pb-16">
          <div className="flex items-center justify-center flex-col sm:flex-row mb-10">
            <FilterDropdown
              default="All Departments"
              label="Filter by Department"
              filterOptions={initialDepartments}
              value={deptFilter}
              setFilter={setDeptFilter}
            />
            <FilterDropdown
              default="All Offices"
              label="Filter by Office"
              filterOptions={initialOffices}
              value={officeFilter}
              setFilter={setOfficeFilter}
            />
          </div>
          {displayJobs.length > 0 ? (
            displayJobs.map((department, idx) => <Department {...department} key={idx} />)
          ) : (
            <div className="flex flex-col items-center px-10 py-2">
              <img src="/icons/careers.svg" alt="" />
              <p className="tra-text-web-body-l mt-4 text-center">
                There are currently no open positions. Please check back later.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
